import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { usePanelAyuda } from '../lib/ayuda.jsx'
import { Status, Spinner, Empty, relTime } from '../components/ui.jsx'

// Estados en los que el proyecto existe pero no se puede abrir. La baja no siempre termina en
// la misma petición: si un recurso no se libera, la fila queda en `deleting` y la remata el
// barrido. Durante esa ventana la tarjeta se muestra —ocultarla haría pensar que ya no está
// cuando todavía queda trabajo— pero no lleva a ninguna parte.
const EN_CURSO = {
  deleting: 'Eliminando…',
  archived: 'Archivado',
  provisioning: 'Creando…',
  reserved: 'Creando…',
}

// La forma de ver (tarjetas o lista) es un gusto personal, no un dato: localStorage y listo.
const vistaGuardada = () => {
  try { return localStorage.getItem('proyectos-vista') === 'lista' ? 'lista' : 'tarjetas' }
  catch { return 'tarjetas' }
}

/** Solo el usuario del correo: en una tabla interna, el dominio es ruido repetido. */
const usuarioDe = (email) => (email ? String(email).split('@')[0] : null)

export function ProjectsIndex() {
  const nav = useNavigate()
  const [vista, setVista] = useState(vistaGuardada)
  const [buscar, setBuscar] = useState('')
  const ayuda = usePanelAyuda()

  const cambiarVista = (v) => {
    setVista(v)
    try { localStorage.setItem('proyectos-vista', v) } catch { /* modo privado */ }
  }

  const q = useQuery({
    queryKey: ['projects'],
    queryFn: api.projects,
    // Mientras algo esté a medias se mira más a menudo, para que la tarjeta desaparezca sola
    // cuando termine en vez de obligar a recargar.
    refetchInterval: (query) =>
      query.state.data?.projects?.some((p) => EN_CURSO[p.state]) ? 3000 : 20_000,
  })

  const todos = q.data?.projects ?? []
  const aguja = buscar.trim().toLowerCase()
  const visibles = aguja
    ? todos.filter((p) =>
        [p.slug, p.domain, p.gitlabPath, p.createdBy].some((c) => c?.toLowerCase().includes(aguja)))
    : todos

  return (
    <div className="main" style={{ margin: '0 auto' }}>
      <div className="section-head" style={{ marginBottom: 18 }}>
        <div>
          <h1 className="h1">Proyectos</h1>
          <p className="lede" style={{ marginBottom: 0 }}>Entornos de desarrollo de CloudTree</p>
        </div>
        <div className="toolbar">
          <button className="btn secondary" title="Servidor MCP de la consola"
                  onClick={() => ayuda?.abrirTema('conectar')}>
            Conectar Claude
          </button>
          <Link className="btn" to="/new">Nuevo proyecto</Link>
        </div>
      </div>

      {todos.length > 0 && (
        <div className="toolbar" style={{ marginBottom: 18 }}>
          <input className="input" placeholder="Buscar por nombre, dominio o creador…"
                 value={buscar} onChange={(e) => setBuscar(e.target.value)}
                 style={{ flex: 1, maxWidth: 420 }} autoFocus />
          <div className="segmentado" role="group" aria-label="Forma de ver">
            <button className={vista === 'tarjetas' ? 'activo' : ''} aria-pressed={vista === 'tarjetas'}
                    onClick={() => cambiarVista('tarjetas')}>Tarjetas</button>
            <button className={vista === 'lista' ? 'activo' : ''} aria-pressed={vista === 'lista'}
                    onClick={() => cambiarVista('lista')}>Lista</button>
          </div>
        </div>
      )}

      {q.isPending && <Spinner />}

      {q.data && !q.data.dokployUp && (
        <div className="alert warn" style={{ marginBottom: 16 }}>
          Dokploy no responde. Se listan los proyectos registrados, sin su estado actual.
        </div>
      )}

      {q.data && todos.length === 0 && (
        <Empty title="Sin proyectos">
          No hay proyectos registrados a los que tengas acceso en GitLab.
        </Empty>
      )}

      {todos.length > 0 && visibles.length === 0 && (
        <Empty title="Nada que coincida">
          Ningún proyecto casa con «{buscar}».
        </Empty>
      )}

      {visibles.length > 0 && vista === 'tarjetas' && (
        <div className="grid-cards">
          {visibles.map((p) => {
            const enCurso = EN_CURSO[p.state]
            const cuerpo = (
              <>
                <div className="name">{p.slug}</div>
                <div className="domain">{p.domain ?? p.gitlabPath ?? 'sin dominio'}</div>
                <div className="meta">
                  {enCurso
                    ? <span className="badge running"><span className="dot" />{enCurso}</span>
                    : <Status status={p.app?.status} />}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--fg-3)' }}
                        title={p.createdBy ?? undefined}>
                    {usuarioDe(p.createdBy) ?? (p.role === 'admin' ? 'Administrador' : 'Miembro')}
                  </span>
                </div>
              </>
            )
            return enCurso ? (
              <div key={p.slug} className="project-card inerte"
                   title={p.state === 'deleting'
                     ? 'Se está eliminando. Desaparecerá al terminar.'
                     : 'Todavía no se puede abrir.'}>
                {cuerpo}
              </div>
            ) : (
              <Link key={p.slug} className="project-card" to={`/p/${p.slug}`}>{cuerpo}</Link>
            )
          })}
        </div>
      )}

      {visibles.length > 0 && vista === 'lista' && (
        <div className="card"><div className="table-scroll">
          <table className="t">
            <thead>
              <tr><th>Nombre</th><th>Dominio</th><th>Estado</th><th>Tu rol</th><th>Creador</th><th>Creado</th></tr>
            </thead>
            <tbody>
              {visibles.map((p) => {
                const enCurso = EN_CURSO[p.state]
                return (
                  <tr key={p.slug} className={enCurso ? undefined : 'link'}
                      onClick={enCurso ? undefined : () => nav(`/p/${p.slug}`)}
                      title={enCurso ? 'Todavía no se puede abrir.' : undefined}>
                    <td style={{ fontFamily: 'var(--mono)', fontWeight: 500 }}>{p.slug}</td>
                    <td className="ellipsis" style={{ color: 'var(--fg-2)', maxWidth: 260 }}>
                      {p.domain ?? p.gitlabPath ?? '—'}
                    </td>
                    <td>
                      {enCurso
                        ? <span className="badge running"><span className="dot" />{enCurso}</span>
                        : <Status status={p.app?.status} />}
                    </td>
                    <td style={{ color: 'var(--fg-2)' }}>{p.role === 'admin' ? 'Administrador' : 'Miembro'}</td>
                    <td style={{ color: 'var(--fg-2)' }} title={p.createdBy ?? undefined}>
                      {usuarioDe(p.createdBy) ?? '—'}
                    </td>
                    <td style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                      {p.createdAt ? relTime(p.createdAt) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div></div>
      )}
    </div>
  )
}
